

namespace ArrayUtils {
	/**
	* shuffles specified array. Modifies argument
	* knuth-shuffle
	* http://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
	*
	*/
	export function shuffle<T>(array:Array<T>):void {
		let currentIndex = array.length;
		let temporaryValue: T;
		let randomIndex: number;

		while (0 !== currentIndex) {
			randomIndex = Math.floor(Math.random() * currentIndex);
			currentIndex -= 1;

			temporaryValue = array[currentIndex];
			array[currentIndex] = array[randomIndex];
			array[randomIndex] = temporaryValue;
		}
	}

	export function equalContent<T>(a1:Array<T>, a2:Array<T>):boolean {
		if (a1===a2) {
			return true;
		}

		if (a1.length != a2.length) {
			return false;
		}

		let l = a1.length;
		for (let i = 0; i < l; i++) {
			if (a1[i] !== a2[i]) {
				return false;
			}
		}

		return true;
	}

	export function max(array:Array<number>):number {
		if (array.length === 0) {
			return NaN;
		}
		let max = Number.NEGATIVE_INFINITY;
		let l = array.length;
		for (let i = 0 ; i < l; i++) {
			let element = array[i];
			if (element > max) {
				max = element
			}
		}
		return max;
	}

	export function min(array:Array<number>):number {
		if (array.length === 0) {
			return NaN;
		}
		let min = Number.POSITIVE_INFINITY;
		let l = array.length;
		for (let i = 0 ; i < l; i++) {
			let element = array[i];
			if (element < min) {
				min = element
			}
		}
		return min;
	}

	export function sum(array:Array<number>):number {
		if (array.length === 0) {
			return NaN;
		}
		let sum = 0;
		let l = array.length;
		for (let i = 0 ; i < l; i++) {
			sum += array[i];
		}
		return sum;
	}

	export function avg(array:Array<number>):number {
		if (array.length === 0) {
			return NaN;
		}
		let sum = 0;
		let l = array.length;
		for (let i = 0 ; i < l; i++) {
			sum += array[i];
		}
		return sum / l;
	}

	export function copy<T>(array:Array<T>):Array<T> {
		return array.concat();
	}

	/**
	* Sorts specified array ascending. Modifies argument/
	*
	*/
	export function sortAscending(array:Array<number>):Array<number> {
		return array.sort((a,b)=>a-b);
	}

	/**
	* Sorts specified array descending. Modifies argument/
	*
	*/
	export function sortDescending(array:Array<number>):Array<number> {
		return array.sort((a,b)=>b-a);
	}

	/**
	* Sorts specified array ascending. Modifies argument/
	*
	*/
	export function sortStringsAscending(array:Array<string>):Array<string> {
		return array.sort((a,b)=>{
			if (a > b) {
				return 1;
			} else if (a < b) {
				return -1;
			} else {
				return 0;
			}
		});
	}

	export function sortStringsDescending(array:Array<string>):Array<string> {
		return array.sort((a,b)=>{
			if (a > b) {
				return -1;
			} else if (a < b) {
				return 1;
			} else {
				return 0;
			}
		});
	}

	/**
	* Reverses specified array. Modifies argument/
	*
	*/
	export function reverse(array:Float32Array, step?:number):Float32Array
	export function reverse<T>(array:Array<T>, step?:number):Array<T>
	export function reverse(array:any, step:number = 1):any {
		let right = array.length - step;
		let left = 0;
		while(left < right) {
			for (let i:number =0; i < step; i++) {
				let tmp = array[left + i];
				array[left + i] = array[right + i];
				array[right + i] = tmp;
			}
			left += step;
			right -= step;
		}
		return array;
	}

	export function percentile(array:Array<number>, percentile:number):number {
		if (array.length === 0) {
			return NaN;
		}
		let sorted = ArrayUtils.sortAscending(ArrayUtils.copy(array));
		let index = Math.ceil((sorted.length - 1) * percentile);
		return sorted[index];
	}

	interface Counter {
		counter: number;
		max: number;
	}

	export function powerArray<T>(a: Array<Array<T>>, mult:number): Array<Array<T>> {
		let result: Array<Array<T>> = [[]];
		while (mult--) {
			result = this.multiplyArrays(result, a);
		}
		return result;
	}

	export function multiplyArrays<T>(a: Array<Array<T>>, b: Array<Array<T>>): Array<Array<T>> {
		let result: Array<Array<T>> = [];
		for (let ae of a) {
			for (let be of b) {
				result.push(ae.concat(be));
			}
		}
		return result;
	}

	export function getArraySubCombination<T>(a: Array<T>, length: number): Array<Array<T>> {
		let result: Array<Array<T>> = [];
		let counters: Array<Counter> = [];
		for (let i = 0; i < length; i++) {
			counters[i] = { max: a.length, counter: 0 };
		}
		do {
			let combination: Array<T> = [];
			for (let i = 0; i < length; i++) {
				combination.push(a[counters[i].counter]);
			}
			result.push(combination);
		} while (ArrayUtils.arraySubCombinationNext(counters, 0));
		return result;
	}

	export function arraySubCombinationNext(counters:Array<Counter>, depth:number):boolean {
		if (depth == counters.length) return false;
		counters[depth].counter++;
		if (counters[depth].counter == counters[depth].max) {
			let ret = ArrayUtils.arraySubCombinationNext(counters, depth + 1);
			if (ret) {
				counters[depth].counter = counters[depth + 1].counter;
			}
			return ret;	
	 	}
	 	return true;
	}

	export function getAllSublines<T>(arrays: Array<Array<T>>, callback: (arr:Array<T>) => void = null): Array<Array<T>> {
		let result: Array<Array<T>> = [];
		let counters: Array<Counter> = [];
		for (let i = 0; i < arrays.length; i++) {
			counters[i] = { max: arrays[i].length, counter: 0 };
		}
		do {
			let combination: Array<T> = [];
			for (let i = 0; i < arrays.length; i++) {
				combination.push(arrays[i][counters[i].counter]);
			}
			if (callback) {
				callback(combination)
			} else {
				result.push(combination);
			}
		} while (ArrayUtils.arraySubLineNext(counters, 0));
		return result;
	}

	export function arraySubLineNext(counters: Array<Counter>, depth: number): boolean {
		if (depth == counters.length) return false;
		let curCounter = counters[depth];
		curCounter.counter++;
		if (curCounter.counter == curCounter.max) {
			curCounter.counter = 0;
			return ArrayUtils.arraySubLineNext(counters, depth + 1);
		}
		return true;
	}
}
